import React, { useState, useEffect } from "react";
import Select from "react-select";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { createNoteAction, getNotesAction } from "../../../../../store/actions/NotesAction";
import { Button, Modal, Spinner } from "react-bootstrap";
import "react-toastify/dist/ReactToastify.css";
import { FaMicrophone } from "react-icons/fa";

import { handleSuccess, showErrorAlert } from "../../../../../services/ErrorHandlingService";
import { getRolesWithModulesAction } from "../../../../../store/actions/RoleAction";
import { getNoteCategoriesAction } from "../../../../../store/actions/NoteCategoriesAction";

const AddNotes = ({ showModal, handleClose, defaultCategory }) => {
  if (!showModal) return null;

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [noteDescription, setNoteDescription] = useState("");
  const [noteType, setNoteType] = useState("");
  const [noteCategory, setNoteCategory] = useState(null);
  // const [selectedRoles, setSelectedRoles] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const patientDetails = useSelector((state) => state.patients?.patientDetails);
  const roles = useSelector((state) => state.role.roles); // Fetch roles from Redux store
  const [isRecording, setIsRecording] = useState(false);
  const [speechLang, setSpeechLang] = useState("en-IN"); // en-IN default
  const medicines = useSelector((state) => state.medicines.medicines?.medicines || []);

  // 🆕 new state fields
  const [medicalType, setMedicalType] = useState("Medical");
  const [selectedRole, setSelectedRole] = useState("");
  const [selectedMedicine, setSelectedMedicine] = useState(null);

  const toggleLanguage = () => {
    setSpeechLang((prev) => (prev === "en-IN" ? "mr-IN" : "en-IN"));
  };

  const noteCategories = useSelector(
    (state) => state.noteCategory.list || []
  );

  useEffect(() => {
    if (showModal && noteCategories.length === 0) {
      dispatch(getNoteCategoriesAction(false)); // pagination=false
    }
  }, [showModal, dispatch]);

  useEffect(() => {
    if (defaultCategory) {
      setNoteCategory({
        value: defaultCategory.id,
        label: defaultCategory.name,
      });
    }
  }, [defaultCategory]);
  

  useEffect(() => {
    // Fetch roles only once when the component mounts
    if (roles.length === 0) {
      dispatch(getRolesWithModulesAction());
    }
  }, [dispatch, roles.length]); // Dependency array ensures the API is called only once

  const startListening = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      alert("Your browser does not support Speech Recognition");
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.lang = speechLang;// or "mr-IN"
    recognition.continuous = false;
    recognition.interimResults = false;

    setIsRecording(true); // Start glow

    recognition.onresult = (event) => {
      const spokenText = event.results[0][0].transcript;
      setNoteDescription((prev) => (prev ? prev + " " + spokenText : spokenText));
    };

    recognition.onend = () => {
      setIsRecording(false); // Stop glow
    };

    recognition.start();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (loading) return;
    setLoading(true);

    const newErrors = {};

    if (!noteDescription.trim()) {
      newErrors.note_description = "Note description is required";
    }

    if (!noteCategory) {
      newErrors.note_category = "Please select category";
    }

    if (!medicalType) {
      newErrors.note_type = "Please select Medical / Non-Medical";
    }

    if (medicalType === "Non-Medical" && !selectedRole) {
      newErrors.role = "Please select role for Non-Medical note";
    }    

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      showErrorAlert("Please fill all required fields");
      setLoading(false);
      return;
    }


    const userDetails = JSON.parse(localStorage.getItem("userDetails"));
    const admin_id = userDetails?.admin_id || 0;

    const patientId = patientDetails?.patient_id;

    if (!patientId) {
      showErrorAlert("No patient selected. Please select a patient before adding notes.");
      return;
    }

    const resolvedNoteType =
      medicalType === "Medical"
        ? "Doctor"
        : selectedRole?.label; // 👈 role name, id नाही

    // const newNote = {
    //   admin_id,
    //   patient_id: patientId,  // get this from somewhere!
    //   note_description: noteDescription,
    //   note_type: noteType,
    //   note_category: noteCategory?.value || "",
    //   sub_role: JSON.stringify(selectedRole.map((role) => role.value)),
    //   // 🆕 include new fields in payload
    //   // medical_type: medicalType,
    //   role_id: medicalType === "Non-Medical" ? selectedRole?.value : null,
    //   medicine_id: selectedMedicine ? selectedMedicine.value : null,
    // };

    const newNote = {
      admin_id,
      patient_id: patientId,

      note_description: noteDescription,

      // ✅ category dropdown मधून id
      category_id: noteCategory.value,

      // ✅ EXACT backend-compatible note_type
      note_type:
        medicalType === "Medical"
          ? "Doctor"
          : selectedRole?.label,

      // optional
      medicine_id: selectedMedicine?.value || null,

      // defaults
      is_important: "No",
      is_emergency: "No",
      prescription_id: 0,
      followup_id: 0,
    };


    try {
      // await dispatch(createNoteAction(newNote, navigate));
      await dispatch(createNoteAction(newNote));

      //handleSuccess("Note added successfully");
      await dispatch(getNotesAction(patientId));

      // Clear fields after success
      setNoteDescription("");
      setNoteType("Self (Doctor)");
      setSelectedRole([]);
      setMedicalType("Medical");
      setSelectedRole(null);
      // handleClose();
      handleClose(selectedMedicine ? selectedMedicine.value : null);


      //navigate("/app-patient-details");
    } catch (error) {
      console.error("Form submission failed:", error);
      showErrorAlert("Failed to add note");
    }
    finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Modal show={showModal} onHide={handleClose}>
        <Modal.Header closeButton>
          <Modal.Title>Add Notes</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="position-relative">
            <textarea
              className="form-control pe-5"
              rows="4"
              value={noteDescription}
              onChange={(e) => setNoteDescription(e.target.value)}
              placeholder="Enter your notes here..."
            />

            {/* MIC BUTTON WITH RECORDING GLOW */}
            <button
              type="button"
              onClick={startListening}
              style={{
                position: "absolute",
                right: "10px",
                bottom: "10px",
                width: "34px",
                height: "34px",
                borderRadius: "50%",
                border: "none",
                background: isRecording ? "#ff4d4d" : "#f1f3f5",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                transition: "0.3s",
                boxShadow: isRecording
                  ? "0 0 10px 3px rgba(255, 0, 0, 0.6)"
                  : "0 2px 4px rgba(0,0,0,0.12)",
                transform: isRecording ? "scale(1.1)" : "scale(1)",
              }}
            >
              <FaMicrophone size={15} color={isRecording ? "white" : "#333"} />
            </button>

            {/* LANGUAGE TOGGLE BUTTON */}
            <button
              type="button"
              onClick={toggleLanguage}
              style={{
                position: "absolute",
                right: "55px",
                bottom: "10px",
                padding: "2px 10px",
                borderRadius: "12px",
                border: "1px solid #ccc",
                background: "#fff",
                fontSize: "12px",
                cursor: "pointer",
              }}
            >
              {speechLang === "en-IN" ? "EN" : "MR"}
            </button>
          </div>

          {/* ⭐ Select Medicine (optional) ⭐ */}
          <div className="mt-3">
            <label className="form-label fw-semibold">Select Medicine (optional):</label>

            <Select
              options={medicines.map((m) => ({
                value: m.medicine_id,
                label: m.medicine_name,
              }))}
              value={selectedMedicine}
              onChange={(option) => setSelectedMedicine(option)}
              placeholder="Choose medicine to add in prescription"
              isClearable
            />
          </div>


          <div className="mt-3">
            <label className="form-label">Category Type:</label>
            <Select
             options={noteCategories.map((cat) => ({
              value: cat.id,
              label: cat.name,
              colour: cat.colour,
              forground_color: cat.forground_color, // ✅ NEW
            }))}
              value={noteCategory}
              onChange={(option) => setNoteCategory(option)}
              placeholder="Select Note Category"
              classNamePrefix="select"
            />

            {errors.note_category && <p className="text-danger">{errors.note_category}</p>}
          </div>

          {/* 🆕 Medical / Non-Medical Radio */}
          <div className="mt-3">
            <label className="form-label">Note Type:</label>
            <div className="d-flex gap-3">
              <div>
                <input
                  type="radio"
                  id="medical"
                  name="medicalType"
                  value="Medical"
                  checked={medicalType === "Medical"}
                  onChange={(e) => setMedicalType(e.target.value)}
                />
                <label htmlFor="medical" className="ms-2">Medical</label>
              </div>
              <div>
                <input
                  type="radio"
                  id="nonMedical"
                  name="medicalType"
                  value="Non-Medical"
                  checked={medicalType === "Non-Medical"}
                  onChange={(e) => setMedicalType(e.target.value)}
                />
                <label htmlFor="nonMedical" className="ms-2">Non-Medical</label>
              </div>
            </div>
          </div>

          {/* 🆕 Show Role dropdown if Non-Medical */}
          {medicalType === "Non-Medical" && (
            <div className="mt-3">
              <label className="form-label">Select Role:</label>
              <Select
                name="role"
                options={roles.map((role) => ({
                  value: role.role_id,
                  label: role.role_name,
                }))}
                value={selectedRole}
                onChange={(opt) => setSelectedRole(opt)}
                placeholder="Select Role"
                classNamePrefix="select"
              />
              {errors.role && <p className="text-danger">{errors.role}</p>}
            </div>
          )}

          {/* <div className="mt-3">
            <label className="form-label">Note for:</label>
            <div>
              <input
                type="radio"
                id="self"
                name="noteType"
                value="Self (Doctor)"
                checked={noteType === "Self (Doctor)"}
                onChange={(e) => setNoteType(e.target.value)}
              />
              <label htmlFor="self" className="ms-2">Self (Doctor)</label>
            </div>
            <div>
              <input
                type="radio"
                id="general"
                name="noteType"
                value="General to All"
                checked={noteType === "General to All"}
                onChange={(e) => setNoteType(e.target.value)}
              />
              <label htmlFor="general" className="ms-2">General to All</label>
            </div>
            <div>
              <input
                type="radio"
                id="selectRole"
                name="noteType"
                value="Select Role"
                checked={noteType === "Select Role"}
                onChange={(e) => setNoteType(e.target.value)}
              />
              <label htmlFor="selectRole" className="ms-2">Select Role</label>
            </div>
          </div> */}

          {/* Multi-select Dropdown for Roles */}
          {noteType === "Select Role" && (
            <div className="mt-3">
              <label className="form-label">Select Sub-Roles:</label>
              <Select
                isMulti
                name="Roles"
                options={roles.map((role) => ({
                  value: role.role_id,
                  label: role.role_name,
                }))}
                value={selectedRoles}
                onChange={(selectedOptions) => setSelectedRoles(selectedOptions || [])}
                placeholder="Select Roles"
                classNamePrefix="select"
              />
            </div>
          )}
        </Modal.Body>

        <Modal.Footer className="d-flex justify-content-between">
          <Button variant="primary" onClick={handleSubmit} disabled={loading}>
            {loading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Adding...
              </>
            ) : (
              "Add"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default AddNotes;
